// Small SVG/DOM widgets for the landing page, ported from the inline script in
// public/landing/index.html. Each function takes the element it draws into, so
// components can call them from mounted() and tear them down on destroy.

const NS = "http://www.w3.org/2000/svg";

/** Create an SVG element with attributes, optionally appending it to a parent. */
export function svgEl(tag, attrs, parent) {
  const e = document.createElementNS(NS, tag);
  for (const k in attrs) e.setAttribute(k, attrs[k]);
  if (parent) parent.appendChild(e);
  return e;
}

export function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// Foot traffic by hour, 7am to 9pm. Shared by the hero sparkline and the
// Adverpeople chart's "rate" mode, which divides counts by this baseline.
export const FOOT_TRAFFIC = [60, 140, 230, 310, 380, 420, 360, 300, 330, 390, 450, 410, 300, 190, 90];

export const AP_HOURS = ["7a", "8a", "9a", "10a", "11a", "12p", "1p", "2p", "3p", "4p", "5p", "6p", "7p", "8p", "9p"];
export const AP_COUNTS = [22, 51, 88, 120, 141, 162, 139, 112, 124, 150, 171, 158, 117, 70, 31];

export const REVENUE_BARS = [22, 28, 25, 34, 31, 40, 38, 45, 52, 49, 60, 71];

/** Monthly revenue bars: plain <i> elements sized by percentage height. */
export function drawRevenueBars(host) {
  if (!host) return;
  host.innerHTML = "";
  REVENUE_BARS.forEach((v) => {
    const i = document.createElement("i");
    i.style.height = v + "%";
    host.appendChild(i);
  });
}

export function isNarrowChart() {
  return window.matchMedia("(max-width:700px)").matches;
}

/**
 * Adverpeople engagement chart.
 * mode "count" plots raw counts; mode "rate" plots counts as a percentage of
 * foot traffic for the same hour.
 */
export function drawEngagementChart(host, mode) {
  if (!host) return;
  host.innerHTML = "";

  // Narrow screens get a taller, narrower plot with fewer hour labels, as on
  // the published page. Callers redraw when the width crosses 700px.
  const nr = isNarrowChart();
  const W = nr ? 340 : 560, H = nr ? 340 : 230, l = nr ? 34 : 40, r = 10, t = 24, b = 28;
  host.setAttribute("viewBox", "0 0 " + W + " " + H);
  const isRate = mode === "rate";
  const vals = isRate ? AP_COUNTS.map((v, i) => Math.round((v / FOOT_TRAFFIC[i]) * 100)) : AP_COUNTS;
  const max = isRate ? 60 : 200;
  const step = isRate ? 20 : 50;
  const x = (i) => l + (i * (W - l - r)) / (vals.length - 1);
  const y = (v) => t + (1 - v / max) * (H - t - b);
  const font = "Inter, sans-serif";

  for (let v = 0; v <= max; v += step) {
    svgEl("line", { class: "ap-grid", x1: l, x2: W - r, y1: y(v), y2: y(v), stroke: "#EEF0F3", "stroke-width": 1 }, host);
    const tx = svgEl(
      "text",
      { class: "ap-axis", x: l - 8, y: y(v) + 4, "text-anchor": "end", "font-size": 11, fill: "#9CA3AF", "font-family": font },
      host
    );
    tx.textContent = isRate ? v + "%" : v;
  }

  AP_HOURS.forEach((h, i) => {
    if (i % (nr ? 3 : 2) !== 0) return;
    const tx = svgEl(
      "text",
      { class: "ap-axis", x: x(i), y: H - 8, "text-anchor": "middle", "font-size": 11, fill: "#9CA3AF", "font-family": font },
      host
    );
    tx.textContent = h;
  });

  const pts = vals.map((v, i) => x(i) + "," + y(v)).join(" ");

  const defs = svgEl("defs", {}, host);
  const lg = svgEl("linearGradient", { id: "apg", x1: 0, x2: 0, y1: 0, y2: 1 }, defs);
  svgEl("stop", { offset: 0, "stop-color": "#5B7DC8", "stop-opacity": 0.18 }, lg);
  svgEl("stop", { offset: 1, "stop-color": "#BF192B", "stop-opacity": 0 }, lg);

  svgEl("polygon", { points: `${x(0)},${y(0)} ${pts} ${x(vals.length - 1)},${y(0)}`, fill: "url(#apg)" }, host);
  svgEl(
    "polyline",
    {
      class: "apline",
      points: pts,
      fill: "none",
      stroke: "#5B7DC8",
      "stroke-width": 2.4,
      "stroke-linejoin": "round",
      "stroke-linecap": "round",
    },
    host
  );

  const pk = vals.indexOf(Math.max(...vals));
  svgEl("circle", { class: "ap-peak-dot", cx: x(pk), cy: y(vals[pk]), r: 5, fill: "#5B7DC8", stroke: "#FFFFFF", "stroke-width": 2.5 }, host);
  const lt = svgEl(
    "text",
    {
      class: "ap-peak-label",
      x: x(pk),
      y: y(vals[pk]) - 12,
      "text-anchor": "middle",
      "font-size": 12,
      "font-weight": 600,
      fill: "#1A1A1A",
      "font-family": font,
    },
    host
  );
  lt.textContent = (isRate ? vals[pk] + "%" : vals[pk]) + " at " + AP_HOURS[pk];
}

/** Format a number as whole Australian dollars. */
export function formatAud(n) {
  return "$" + Math.round(n).toLocaleString("en-AU");
}

/** Monthly subscription used by the payback calculation. */
export const PLAN_MONTHLY = 69;

export function roiFigures(rate, advertisers) {
  const monthly = Number(rate) * Number(advertisers);
  return {
    rate: formatAud(Number(rate)),
    advertisers: String(advertisers),
    payback: (monthly / PLAN_MONTHLY).toFixed(1) + " months",
    net: formatAud((monthly - PLAN_MONTHLY) * 12),
  };
}
